// vi.ts — Vietnamese catalog for the owner dashboard.
//
// Translated per docs/superpowers/specs/locale-glossary-vi.md (binding glossary + tone
// contract) as Task 15 of plan 2026-08-31-locale-dashboard. Every key from `en.ts` is spelled
// out here as a literal Vietnamese value — the placeholder object spread that used to stand in
// for it is gone, so `satisfies Catalog` is the real proof every key was translated, not just
// typed (locales.test.ts also guards against that spread coming back, and leakage.test.ts
// guards against untranslated English leaking through instead).
//
// Function-valued entries keep their exact parameter names and interpolate every argument they
// need; Vietnamese has no plural inflection, so every `n === 1 ? 'x' : 'xs'` ternary in en.ts
// collapses to one fixed form here (glossary §6) — the parameter itself is still threaded
// through the signature even where it no longer drives a branch.
import { type Catalog } from './en';

export const vi = {
  common: {
    close: 'Đóng',
  },
  status: {
    pending: 'đang chờ',
    crawling: 'đang quét',
    indexing: 'đang lập chỉ mục',
    ready: 'sẵn sàng',
    error: 'lỗi',
    paused: 'tạm dừng',
    hint: 'gợi ý',
    deleting: 'đang xóa',
  },
  time: {
    never: 'chưa bao giờ',
    justNow: 'vừa xong',
    inAMoment: 'trong giây lát',
    secondsAgo: ({ n }: { n: number }) => `${n} giây trước`,
    inSeconds: ({ n }: { n: number }) => `sau ${n} giây`,
    minutesAgo: ({ n }: { n: number }) => `${n} phút trước`,
    inMinutes: ({ n }: { n: number }) => `sau ${n} phút`,
    hoursAgo: ({ n }: { n: number }) => `${n} giờ trước`,
    inHours: ({ n }: { n: number }) => `sau ${n} giờ`,
    daysAgo: ({ n }: { n: number }) => `${n} ngày trước`,
    inDays: ({ n }: { n: number }) => `sau ${n} ngày`,
    monthsAgo: ({ n }: { n: number }) => `${n} tháng trước`,
    inMonths: ({ n }: { n: number }) => `sau ${n} tháng`,
    yearsAgo: ({ n }: { n: number }) => `${n} năm trước`,
    inYears: ({ n }: { n: number }) => `sau ${n} năm`,
  },
  shell: {
    impersonating: ({ email }: { email: string }) =>
      `Đang xem với vai trò ${email} — bạn đang mạo danh người dùng này.`,
    stopImpersonating: 'Dừng',

    platformAdmin: 'Quản trị nền tảng',
    adminTabWorkspaces: 'Workspace',
    adminTabBots: 'Bot',
    adminTabJobs: 'Tác vụ',
    adminTabPosts: 'Bài viết',
    adminTabFeedback: 'Phản hồi',
    adminTabPlayground: 'Playground',

    botsLoadError: 'Không tải được danh sách bot của bạn.',
    botsLoadErrorInline: 'Không tải được danh sách bot của bạn',
    reloadToRetry: 'Tải lại trang để thử lại.',

    navOverview: 'Tổng quan',
    navUnanswered: 'Câu chưa trả lời được',
    navConversations: 'Cuộc trò chuyện',
    navLeads: 'Lead',
    navSources: 'Nguồn',
    navPlayground: 'Playground',
    navAppearance: 'Giao diện',
    navSettings: 'Cài đặt',
    groupNeedsYou: 'CẦN BẠN XỬ LÝ',
    groupKnowledge: 'KIẾN THỨC',
    groupSetup: 'THIẾT LẬP',

    closeMenu: 'Đóng menu',
    navigationLabel: 'Điều hướng',
    openNavigation: 'Mở điều hướng',
    openAccountMenu: 'Mở menu tài khoản',

    switchBotAriaLabel: ({ name }: { name: string }) => `${name}, chuyển bot`,
    botSwitcherHeader: ({ n, cap }: { n: number; cap: number | null }) =>
      `BOT CỦA BẠN${cap !== null ? ` · ĐÃ DÙNG ${n}/${cap}` : ''}`,
    allBots: 'Tất cả bot',
    newBot: 'Bot mới',
    newBotOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `Bot mới — đã dùng ${n}/${cap}`,

    pagesIndexed: 'Trang đã lập chỉ mục',

    feedbackTrigger: 'Phản hồi',
    feedbackTypeGroupLabel: 'Loại phản hồi',
    feedbackTypeBug: 'Lỗi',
    feedbackTypeFeature: 'Đề xuất tính năng',
    feedbackTypeQuestion: 'Câu hỏi',
    feedbackPlaceholder: 'Chuyện gì đã xảy ra, hoặc điều gì sẽ giúp Embent tốt hơn cho bạn?',
    feedbackSent: 'Đã gửi — chúng tôi đọc từng phản hồi một.',
    feedbackSendError: 'Không gửi được phản hồi.',
    feedbackSending: 'Đang gửi…',
    feedbackSend: 'Gửi',
    feedbackDialogTitle: 'Gửi phản hồi',
    feedbackDialogDescription: 'Lỗi, đề xuất tính năng, hay câu hỏi — nội dung này gửi thẳng đến đội ngũ.',

    workspaceSettings: 'Cài đặt workspace',
    members: 'Thành viên',
    billingUsage: 'Thanh toán & sử dụng',
    switchWorkspace: 'Chuyển workspace',
    accountLabelPrefix: 'Tài khoản — ',
    account: 'Tài khoản',
    signOut: 'Đăng xuất',
    themePrefix: 'Giao diện: ',
    languagePrefix: 'Ngôn ngữ: ',
    nextPrefix: 'tiếp theo: ',
    themeSystem: 'Theo hệ thống',
    themeDark: 'Tối',
    themeLight: 'Sáng',
    roleOwner: 'Chủ sở hữu',
    roleAdmin: 'Quản trị',
    roleMember: 'Thành viên',
    workspaceSubline: ({ plan, role }: { plan: string | null; role: string }) =>
      plan ? `${plan} · bạn là ${role}` : `Bạn là ${role}`,
    workspaceMenuAriaLabel: ({ name }: { name: string }) => `${name}, menu workspace và tài khoản`,

    usageWarning: ({ pct }: { pct: number }) =>
      `Bạn đã dùng ${pct}% số tin nhắn trong tháng — nâng cấp để mọi cuộc trò chuyện không bị gián đoạn.`,
    dismiss: 'Bỏ qua',

    renewsOn: ({ date }: { date: string }) => `Gia hạn ${date}`,
    resetsOn: ({ date }: { date: string }) => `Đặt lại ${date}`,
    answersPaused: 'Tạm dừng trả lời mới — đã vượt 110% giới hạn',
    messagesLeft: ({ n }: { n: string }) => `Còn ${n} tin nhắn trong tháng này`,
    badgeStillShown: ({ plan }: { plan: string }) => `${plan} vẫn hiển thị huy hiệu "Powered by Embent"`,
    badgeOptional: ({ plan }: { plan: string }) => `${plan} cho phép tắt huy hiệu "Powered by Embent"`,
    upgradeCta: ({ price }: { price: number }) => `Nâng cấp — $${price}/tháng`,
    resumeAnswersCta: ({ price }: { price: number }) => `Tiếp tục trả lời — $${price}/tháng`,
    resumeAnswersBenefit: 'Nâng cấp sẽ tiếp tục trả lời ngay lập tức',
    upgradeBenefit: ({
      plan,
      messages,
      bots,
      seats,
    }: {
      plan: string;
      messages: string;
      bots: number;
      seats: number;
    }) => `${plan} thêm ${messages} tin nhắn, ${bots} bot và ${seats} chỗ`,
  },
  bots: {
    noSources: 'không có nguồn',
    allSourcesReady: ({ n }: { n: number }) => `${n} · đã sẵn sàng`,
    sourceStatusRollup: ({ n, status }: { n: number; status: string }) => `${n} · ${status}`,
    paused: 'Tạm dừng',
    sourcesRemoving: ({ n }: { n: number }) => `${n} nguồn · đang xóa`,
    sourcesSyncError: ({ n }: { n: number }) => `${n} nguồn · lỗi đồng bộ`,
    sourcesSyncing: ({ n }: { n: number }) => `${n} nguồn · đang đồng bộ`,
    messagesUnanswered: ({ messages, unanswered }: { messages: number; unanswered: number }) =>
      `${messages} tin nhắn · ${unanswered} chưa trả lời được`,
    botsCount: ({ n }: { n: number }) => `${n} bot`,
    botsOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `Đã dùng ${n}/${cap}`,

    colBot: 'Bot',
    colStatus: 'Trạng thái',
    colMessages14d: 'Tin nhắn · 14 ngày',
    colUnanswered: 'Chưa trả lời được',
    colSources: 'Nguồn',
    pageTitle: 'Bot',
    emptyTitle: 'Tạo bot đầu tiên của bạn',
    emptyDescription:
      'Trỏ nó vào website hoặc tài liệu của bạn — nó sẽ quét nội dung và bắt đầu trả lời câu hỏi của khách truy cập.',
    planIncludesBots: ({ cap }: { cap: number }) => `Gói của bạn bao gồm ${cap} bot`,
    upgradeGateDescription: ({ name, priceUsd, maxBots }: { name: string; priceUsd: number; maxBots: number }) =>
      `${name} ($${priceUsd}/tháng) chạy tối đa ${maxBots} bot — mỗi site một bot, mỗi bot có kiến thức và giao diện riêng.`,
    comparePlansLink: 'So sánh các gói →',
    newBotDialogTitle: 'Bot mới',
    botNameFieldLabel: 'Tên',
    botNamePlaceholder: 'Bot hỗ trợ Acme',
    creatingCta: 'Đang tạo…',
    createCta: 'Tạo',
  },
  auth: {
    emailLabel: 'Email',
    passwordLabel: 'Mật khẩu',
    genericFormError: 'Đã có lỗi xảy ra. Vui lòng thử lại.',
    continueWithGoogle: 'Tiếp tục với Google',
    backToSignIn: 'Quay lại đăng nhập',

    signInTitle: 'Đăng nhập',
    signInSubtitle: 'Chào mừng bạn quay lại Embent.',
    passwordJustResetNotice: 'Mật khẩu đã cập nhật — đăng nhập bằng mật khẩu mới của bạn.',
    forgotPasswordLink: 'Quên mật khẩu?',
    unverifiedResendNotice: 'Kiểm tra hộp thư — chúng tôi đã gửi lại email xác minh.',
    signingIn: 'Đang đăng nhập…',
    signInCta: 'Đăng nhập',
    noAccountPrompt: 'Chưa có tài khoản? ',
    signUpLink: 'Đăng ký',

    signUpTitle: 'Tạo tài khoản của bạn',
    signUpSubtitle: 'Bắt đầu trả lời câu hỏi của khách truy cập chỉ trong vài phút.',
    nameLabel: 'Tên',
    creatingAccount: 'Đang tạo tài khoản…',
    createAccountCta: 'Tạo tài khoản',
    haveAccountPrompt: 'Đã có tài khoản? ',
    signInLink: 'Đăng nhập',

    checkEmailTitle: 'Kiểm tra email của bạn',
    checkEmailSubtitle: 'Chúng tôi đã gửi cho bạn một link xác minh. Link thường đến trong vòng một phút.',
    checkEmailBody: 'Nhấp vào link trong email để kích hoạt tài khoản của bạn.',

    verifyFailedTitle: 'Xác minh thất bại',
    verifiedTitle: 'Email đã xác minh',
    verifyFailedBody: 'Link xác minh này không hợp lệ hoặc đã hết hạn.',
    verifiedBody: 'Xong rồi — đang đưa bạn đến dashboard…',

    requestResetTitle: 'Đặt lại mật khẩu của bạn',
    requestResetSubtitlePending: 'Nhập email của bạn và chúng tôi sẽ gửi link đặt lại.',
    requestResetSubtitleSent: 'Nếu email đó có tài khoản Embent, chúng tôi đã gửi link đặt lại.',
    requestResetBodySent: 'Kiểm tra hộp thư của bạn. Link hết hạn sau 1 giờ.',
    sendingResetLink: 'Đang gửi…',
    sendResetLinkCta: 'Gửi link đặt lại',

    resetLinkInvalidTitle: 'Link đặt lại không hợp lệ',
    resetLinkInvalidBody: 'Link đặt lại mật khẩu này thiếu token, hoặc đã được dùng rồi.',
    requestNewLinkCta: 'Yêu cầu link mới',
    chooseNewPasswordTitle: 'Chọn mật khẩu mới',
    chooseNewPasswordSubtitle: 'Chọn một mật khẩu bạn chưa từng dùng trước đây.',
    newPasswordLabel: 'Mật khẩu mới',
    resetLinkExpiredFallback: 'Link đặt lại đó không hợp lệ hoặc đã hết hạn.',
    savingPassword: 'Đang lưu…',
    saveNewPasswordCta: 'Lưu mật khẩu mới',

    inviteNotValidTitle: 'Lời mời không hợp lệ',
    goToEmbentLink: 'Đến Embent',
    inviteInvalidFallback: 'Lời mời này không còn hiệu lực.',
    inviteTitle: ({ workspaceName }: { workspaceName: string }) => `Bạn được mời vào ${workspaceName}`,
    inviteDescription: ({ email }: { email: string }) =>
      `Đã gửi đến ${email} — đăng nhập hoặc tạo tài khoản bằng email đó để chấp nhận.`,
    inviteSignInCta: 'Đăng nhập',
    inviteSignUpCta: 'Tạo tài khoản',
    wrongAccountTitle: 'Sai tài khoản',
    signOutCta: 'Đăng xuất',
    inviteAcceptFallback: 'Đã có lỗi khi chấp nhận lời mời này.',
    joiningWorkspaceTitle: ({ workspaceName }: { workspaceName: string }) => `Đang tham gia ${workspaceName}…`,
    joiningWorkspaceBody: 'Chờ một chút.',

    eventLabels: {
      newLead: 'lead mới',
      unansweredDigest: 'tổng hợp câu chưa trả lời được',
      crawlFailed: 'quét thất bại',
      weeklySummary: 'tổng kết hàng tuần',
    },
    unsubscribeLinkExpiredTitle: 'Link này đã hết hạn',
    unsubscribeLinkExpiredBody: 'Quản lý thông báo trong phần cài đặt của bot trên dashboard.',
    openDashboardCta: 'Mở dashboard',
    unsubscribedTitle: 'Bạn đã hủy đăng ký',
    unsubscribedBody: ({ eventLabel, botName }: { eventLabel: string; botName: string | null }) =>
      `Bạn sẽ không nhận email ${eventLabel} cho ${botName ?? 'bot này'} nữa. Bạn có thể bật lại trong phần cài đặt của bot.`,

    notFoundEyebrow: 'Không tìm thấy trang',
    notFoundTitle: 'Không có gì ở địa chỉ này',
    notFoundBody: 'Link có thể đã cũ, hoặc trang đã được chuyển đi.',
    notFoundCta: 'Đến danh sách bot của bạn',

    landingBotsErrorFallback: 'Đã có lỗi khi tải danh sách bot của bạn.',
    landingGoToBotsCta: 'Đến danh sách bot của bạn →',
  },
  onboarding: {
    skipForNow: 'Bỏ qua bước này',
    invalidWebsiteUrl: 'Nhập một địa chỉ website hợp lệ, ví dụ https://acme.vn',
    websiteUrlLabel: 'Địa chỉ website',
    genericFallbackError: 'Đã có lỗi xảy ra.',

    crawlDefaultHostname: 'site của bạn',
    noSourceToReplace: 'Chưa có nguồn nào để thay thế.',

    stepWebsite: 'Website',
    stepCrawl: 'Quét',
    stepTryIt: 'Dùng thử',
    stepLook: 'Giao diện',
    stepGoLive: 'Lên sóng',
    stepMobileReadout: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Bước ${current}/${total} · ${label}`,
    stepProgressAriaLabel: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Tiến trình thiết lập: bước ${current}/${total}, ${label}`,
    stepTablistAriaLabel: 'Tiến trình thiết lập',

    websiteHeading: 'Thêm website của bạn',
    websiteSubtitle:
      'Embent đọc site của bạn và dựng một bot trả lời kèm trích dẫn nguồn — không bịa đặt gì cả. Hầu hết các site sẵn sàng chỉ sau vài phút.',
    botNameLabel: 'Tên bot',
    botNamePlaceholder: 'Bot hỗ trợ Acme',
    websiteUrlPlaceholder: 'acme.vn',
    websiteUrlHelper: 'Chúng tôi sẽ tự động tìm các trang của bạn. Bạn có thể thêm nguồn khác sau.',
    botNameRequired: 'Nhập tên cho bot của bạn.',
    websiteStarting: 'Đang bắt đầu…',
    websiteSubmitCta: 'Bắt đầu đọc site của tôi',

    crawlHeading: ({ hostname }: { hostname: string }) => `Đang đọc ${hostname}…`,
    crawlSubtitleReady: 'Xong rồi — mọi trang đã được lập chỉ mục và sẵn sàng trả lời.',
    crawlSubtitleSyncing: 'Bạn không cần chờ ở đây — cứ dùng thử bot trong lúc nó hoàn tất.',
    crawlStatusLines: {
      pending: ['Đang xếp hàng — sắp bắt đầu…', 'Đang chuẩn bị bắt đầu…'],
      crawling: ['Đang tìm trang trên site…', 'Đang lần theo liên kết…', 'Đang đọc các trang…'],
      indexing: ['Đang lập chỉ mục tìm kiếm…', 'Đang xử lý nội dung đã đọc…'],
    },
    crawlPagesIndexed: ({ n }: { n: number }) => `${n} trang đã lập chỉ mục`,
    crawlNoSourceYet: 'Chúng tôi chưa tìm thấy nguồn nào cho bot này.',
    crawlErrorLead:
      'Nguyên nhân thường gặp nhất là gõ sai địa chỉ — kiểm tra lại bên dưới rồi thử lại.',
    crawlReplaceUrlCta: 'Thử một địa chỉ khác',
    crawlReplaceUrlUpdating: 'Đang cập nhật…',
    crawlReplaceUrlHelper: 'Chúng tôi sẽ xóa lượt thử thất bại và bắt đầu lại với địa chỉ này.',
    crawlRetryCta: 'Thử lại',
    crawlRetrying: 'Đang thử lại…',
    crawlRetryHelper:
      'Nếu địa chỉ đúng và site chỉ tạm thời không truy cập được, hãy thử lại. Những site cần ' +
      'JavaScript để hiển thị nội dung có thể xử lý bằng một tùy chọn có sẵn sau khi thiết lập, ' +
      'ở màn hình Nguồn.',
    crawlCtaSyncing: 'Dùng thử bot trong lúc hoàn tất →',
    crawlCtaReady: 'Tiếp theo: dùng thử bot →',

    tryItHeading: 'Hỏi nó bất cứ điều gì',
    tryItSubtitleWithPages: ({ n }: { n: number }) =>
      `Nó đã biết ${n} trang — và vẫn đang học thêm phần còn lại. Mỗi câu trả lời đều kèm nguồn.`,
    tryItSubtitleNoPages:
      'Nó vẫn đang đọc site của bạn — cứ thử hỏi một câu; câu trả lời sẽ chỉ tốt hơn từ đây.',
    tryItSamplePrompt: `Thử: "Chính sách đổi trả của bạn thế nào?"`,
    tryItPlaceholder: 'Hỏi về vận chuyển, giá cả, bất cứ điều gì…',
    tryItSending: 'Đang gửi…',
    tryItSendCta: 'Gửi',
    tryItNextCta: 'Ổn rồi — làm cho giống tôi',

    lookHeading: 'Tạo giao diện khớp với site của bạn',
    lookSubtitle:
      'Chọn màu thương hiệu của bạn — bản xem trước cập nhật khi bạn gõ. Bạn có thể tinh chỉnh mọi thứ sau ở mục Giao diện.',
    brandColorLabel: 'Màu thương hiệu',
    colorHexAriaLabel: 'Mã màu hex',
    welcomeMessageLabel: 'Lời chào',
    lookSaving: 'Đang lưu…',
    lookContinueCta: 'Tiếp tục',

    goLiveHeading: 'Chỉ một đoạn mã và bạn đã lên sóng',
    goLivePasteBeforePrefix: 'Dán đoạn này trước thẻ đóng',
    goLivePasteBeforeSuffix: 'trên site của bạn. Vậy là xong — bot sẽ xuất hiện ở góc dưới bên phải.',
    goLiveInstallGuidesLink: 'Hướng dẫn cài đặt theo nền tảng',
    goLiveFirstAnswerNotice: 'Chúng tôi sẽ gửi email cho bạn ngay khi widget trả lời vị khách đầu tiên.',
    goLiveDashboardCta: 'Đến dashboard',
    goLiveViewSourcesCta: 'Xem nguồn',
  },
  overview: {
    title: 'Tổng quan',
    liveOnPrefix: 'Đang hoạt động trên ',
    answeringSince: ({ date }: { date: string }) => `trả lời từ ${date}`,
    testInPlaygroundCta: 'Dùng thử trong Playground',
    addSourcesCta: 'Thêm nguồn',
    botDetailsLoadError: ({ message }: { message: string }) => `Không tải được thông tin bot — ${message}`,

    messagesLabel: 'Tin nhắn · 14 ngày qua',
    conversationsLink: 'Cuộc trò chuyện →',
    vsPreviousPeriod: 'so với 14 ngày trước đó',
    leadsLabel: 'Lead đã bắt được',
    newLeadsCount: ({ n }: { n: number }) => `${n} mới`,
    leadsThisWeek: ({ n }: { n: number }) => `${n} tuần này`,
    viewLeadsLink: 'Xem lead →',
    unansweredLabel: 'Câu chưa trả lời được',
    fixThemLink: 'Xử lý ngay →',

    knowledgeHeading: 'Kiến thức',
    pointAtSite: 'Trỏ nó vào site của bạn',
    addSourceLink: 'Thêm nguồn →',
    addSourcesLink: 'Thêm nguồn →',
    pagesIndexed: 'Trang đã lập chỉ mục',
    sourcesCountLabel: 'Nguồn',
    lastSyncedLabel: 'Đồng bộ lần cuối',
    manageSourcesLink: 'Quản lý nguồn →',
    resyncing: 'Đang đồng bộ lại…',
    allSyncing: 'Đang đồng bộ tất cả',
    resyncCta: 'Đồng bộ lại',
    resyncNothingToSync: 'Không có gì để đồng bộ — mọi nguồn đều đang đồng bộ rồi.',
    resyncGenericError: 'Không bắt đầu đồng bộ lại được — vui lòng thử lại.',

    latestConversationsHeading: 'Cuộc trò chuyện gần đây',
    allConversationsLink: 'Tất cả cuộc trò chuyện →',
    noConversationsEmpty: 'Yên ắng trong 14 ngày qua — cuộc trò chuyện mới sẽ hiện ở đây.',
    noMessageFallback: '(không có tin nhắn)',
    leadCapturedTag: 'Đã bắt lead',
    hasUnansweredTag: 'Có câu chưa trả lời được',

    knowledgeAllReady: 'Đã sẵn sàng',
    knowledgeSyncing: 'Nguồn đang đồng bộ',
    knowledgeNeedsAttention: 'Có nguồn cần chú ý',
    knowledgeRemoving: 'Đang xóa nguồn',

    createdSubtitle: ({ time }: { time: string }) => `Tạo lúc ${time}`,
    zeroStatMessagesHint: 'Bắt đầu từ câu hỏi đầu tiên',
    zeroStatLeadsHint: 'Tên và email, giữa cuộc trò chuyện',
    zeroStatUnansweredHint: 'Chưa có gì cần xử lý',
    heroReadyHeadline: 'Bot của bạn đã sẵn sàng trả lời',
    heroIndexingHeadline: 'Vẫn đang đọc site của bạn',
    heroEmptyHeadline: 'Thêm một nguồn để bắt đầu',
    heroEmptyBody:
      'Nó chưa có gì để đọc cả. Thêm một nguồn và nó sẽ sẵn sàng trả lời chỉ sau vài phút.',
    heroIndexingErrorBody:
      'Có nguồn cần chú ý — nó sẽ không tự hoàn tất được. Kiểm tra thẻ Kiến thức để xem vấn đề, rồi đưa lên site của bạn sau khi đã khắc phục.',
    heroIndexingPrefix: 'Nó vẫn đang đọc',
    heroIndexingNoSourceFallback: 'nguồn của bạn',
    heroIndexingSuffix: ' — quay lại kiểm tra sau vài phút, rồi đưa lên site của bạn.',
    heroReadyBodyPrefix: ({ n }: { n: number }) => `Nó đã đọc ${n} trang`,
    heroReadyFromDomain: ' từ ',
    heroReadyBodySuffix:
      ' và biết đủ để trả lời câu hỏi về những trang đó. Còn một bước nữa: đưa nó lên site của bạn.',
    onlyPagesNudge: ({ n }: { n: number }) =>
      `Chỉ ${n} trang thôi à? Thêm trang FAQ, bảng giá hoặc chính sách để nó có thêm nội dung để dùng.`,
    addSourceCta: 'Thêm nguồn',
    addSourceHelperText:
      'Mất khoảng một phút. Đoạn mã nhúng sẽ chờ sẵn ở đây ngay khi nó có nội dung để trả lời.',
    pasteBeforeBodyLabel: 'DÁN ĐOẠN NÀY TRƯỚC </BODY>',
    emailSnippetLink: 'Gửi đoạn mã này qua email cho developer của tôi',
    emailSnippetSubject: ({ botName }: { botName: string }) => `Gắn ${botName} lên site của chúng tôi`,
    emailSnippetBody: ({ snippet }: { snippet: string }) => `Dán đoạn này trước thẻ đóng </body>:\n\n${snippet}`,
    whatHappensNextHeading: 'Điều gì xảy ra tiếp theo',
    stepVisitorAsksTitle: 'Một khách truy cập hỏi điều gì đó',
    stepVisitorAsksBody: 'Bong bóng chat nằm ở góc dưới bên phải mọi trang có gắn đoạn mã. Không cần thiết lập gì thêm.',
    stepEmailAnswerTitle: 'Chúng tôi gửi email cho bạn câu trả lời đầu tiên',
    stepEmailAnswerBody: 'Để bạn đọc đúng những gì nó đã trả lời, bằng lời của khách hàng, trước khi bất kỳ ai khác thấy.',
    stepUnansweredTitle: 'Những gì nó không trả lời được sẽ vào đây',
    stepUnansweredBody: 'Nó từ chối trả lời thay vì bịa đặt. Mỗi lần từ chối trở thành một việc cần bạn xử lý.',
    stepAddSourceTitle: 'Thêm một nguồn',
    stepAddSourceBody:
      'Trỏ nó vào site của bạn, hoặc tải lên tệp và dán văn bản — nó cần có gì đó để đọc trước khi có thể trả lời.',
    stepAddSourceVisitorTitle: 'Khách truy cập hỏi, bạn nhận email chứa câu trả lời',
    countersHeading: 'BỘ ĐẾM BẮT ĐẦU TỪ KHÁCH TRUY CẬP ĐẦU TIÊN CỦA BẠN',

    needsYouHeading: 'Cần bạn xử lý',
    allUnansweredLink: 'Tất cả câu chưa trả lời được →',
    needsYouEmpty: 'Không có gì cần xử lý — bot của bạn đã trả lời mọi câu được hỏi.',
    needsYouIntro: 'Bot của bạn đã nói là không biết. Trả lời một lần và nó sẽ nhớ mãi.',
    askedSummary: ({ n, time }: { n: number; time: string }) => `Hỏi ${n} lần · lần cuối ${time}`,
    writeAnswerCta: 'Viết câu trả lời',
    cancelCta: 'Hủy',
    answerPlaceholder: 'Viết câu trả lời bạn sẽ đưa cho khách truy cập này. Bot sẽ dùng nó từ giờ trở đi.',
    addingCta: 'Đang thêm…',
    addAsQaSourceCta: 'Thêm làm nguồn Hỏi & Đáp',
    saveAnswerError: 'Không lưu được câu trả lời đó — vui lòng thử lại.',
  },
  sources: {
    pageTitle: 'Nguồn',
    sourcesSubtitle: ({
      n,
      pageCount,
      lastSynced,
    }: {
      n: number;
      pageCount: number;
      lastSynced: string;
    }) => `${n} nguồn · ${pageCount} trang · đồng bộ lần cuối ${lastSynced}`,
    botLoadError: ({ message }: { message: string }) => `Không tải được bot này — ${message}`,

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Văn bản',
    typeFile: 'Tệp',

    colType: 'Loại',
    colUri: 'URI',
    colStatus: 'Trạng thái',
    colRenderedMode: 'Chế độ render',
    colPages: 'Trang',
    colLastSynced: 'Đồng bộ lần cuối',
    colActions: 'Hành động',

    urlFieldLabel: 'URL trang',
    urlPlaceholder: 'https://example.com/docs',
    sitemapFieldLabel: 'URL sitemap',
    sitemapPlaceholder: 'https://example.com/sitemap.xml',
    textTitleFieldLabel: 'Tiêu đề',
    textTitlePlaceholder: 'Chính sách hoàn tiền',
    textContentFieldLabel: 'Nội dung',
    textContentPlaceholder: 'Dán văn bản bạn muốn bot biết…',
    fileFieldLabel: 'Tệp',
    fileDropPrompt: 'Thả một tệp vào đây, hoặc nhấp để chọn',
    fileDropCaption: ({ extensions, maxSize }: { extensions: string; maxSize: string }) =>
      `${extensions} · tối đa ${maxSize}`,
    fileTooLarge: ({ maxSize }: { maxSize: string }) => `Tệp đó lớn hơn giới hạn ${maxSize}.`,
    fileTypeNotSupported: ({ ext, extensions }: { ext: string; extensions: string }) =>
      `${ext ? `.${ext}` : 'Tệp đó'} không được hỗ trợ — dùng ${extensions}.`,
    filesDroppedNote: ({ name, count }: { name: string; count: number }) =>
      `Đang dùng "${name}" — ${count} tệp khác đã thả vào, bị bỏ qua.`,
    fileDropNotAFile: 'Đó không phải là tệp — kéo một tệp từ máy tính của bạn vào.',
    fileTitleFieldLabel: 'Tiêu đề (không bắt buộc)',
    fileTitlePlaceholder: 'Mặc định theo tên tệp',

    addingCta: 'Đang thêm…',
    addSourceCta: 'Thêm nguồn',

    emptyTitle: 'Trỏ nó vào site của bạn',
    emptyDescription: 'Thêm một URL, sitemap, văn bản hoặc tệp ở trên và chúng tôi sẽ bắt đầu đọc.',

    reviewPagesLink: 'Xem lại các trang',
    nearEmptyInfoAriaLabel: ({ count }: { count: number }) => `${count} trang gần như trống`,

    resyncCta: 'Đồng bộ lại',
    deleteCta: 'Xóa',
    cancelCta: 'Hủy',
    syncInProgressError: 'Đang đồng bộ — thử lại sau',

    deleteConfirmTitle: 'Xóa nguồn này?',
    deleteConfirmDescriptionPrefix: 'Sẽ xóa ',
    deleteConfirmDescriptionSuffix: ' và mọi thứ đã lập chỉ mục từ nó. Không thể hoàn tác.',

    renderedModeExplainerAriaLabel: 'Chế độ render là gì?',
    renderedModeExplainerBody1Bold: 'Chế độ render',
    renderedModeExplainerBody1Rest:
      ' chạy từng trang trong một trình duyệt thật trước khi đọc, để nội dung chỉ xuất hiện sau khi JavaScript chạy cũng được ghi nhận.',
    renderedModeExplainerBody2:
      'Để tắt với các site thông thường — nhanh và rẻ hơn. Bật lên khi một trang trông trống với crawler (single-page app, dashboard, hoặc bất cứ gì tải nội dung sau khi trang đã mở).',
    renderedModeExplainerBody3:
      'Đánh đổi: quét chậm hơn và dùng nhiều hạn mức trang hơn mỗi lần chạy.',

    renderedModeLabel: 'Chế độ render',
    modeRendered: 'Render',
    modeStandard: 'Chuẩn',
    switchModeTitle: ({ mode }: { mode: string }) => `Chuyển sang chế độ ${mode}?`,
    switchModeDescription: ({ mode }: { mode: string }) =>
      `Việc này sẽ quét lại nguồn ở chế độ ${mode}. Các trang có nội dung khác đi sẽ được lập chỉ mục lại và những trang không còn tồn tại sẽ bị xóa — chỉ mục hiện tại vẫn dùng được cho đến khi lượt quét mới hoàn tất.`,
    switchModeCta: ({ mode }: { mode: string }) => `Chuyển sang chế độ ${mode}`,
  },
  sourceDetail: {
    crawledPages: ({ n }: { n: number }) => `Đã quét ${n} trang.`,
    crawledPagesWithSkipped: ({ n, failedCount }: { n: number; failedCount: number }) =>
      `Đã quét ${n} trang. ${failedCount} trang không tải được và đã bị bỏ qua.`,
    truncatedCapacity: ({ n }: { n: number }) =>
      `Đã quét ${n} trang trước khi hết dung lượng quét của chúng tôi. Đây là sự cố tạm thời từ phía chúng tôi — đợi vài phút rồi chạy lại lượt quét.`,
    truncatedDeadline: ({ n }: { n: number }) =>
      `Đã quét ${n} trang trước khi lượt quét này chạm giới hạn thời gian của chúng tôi.`,
    truncatedAttempts: ({ n }: { n: number }) =>
      `Đã quét ${n} trang trước khi lượt quét hết số lần thử với các trang không tải được.`,
    truncatedPageCap: ({ n }: { n: number }) =>
      `Đã quét ${n} trang — mức tối đa mà hạn mức trang của gói bạn cho phép ngay bây giờ.`,
    remainingAtLeast: ({ n }: { n: number }) => ` Ít nhất ${n} trang chưa được lập chỉ mục.`,
    remainingNone: ' Một số trang có thể chưa được lập chỉ mục.',
    crawlFailedGeneric: 'Lượt quét này không lập chỉ mục được trang nào.',

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Văn bản',
    typeFile: 'Tệp',

    languagePrefix: 'Ngôn ngữ: ',

    factType: 'Loại',
    factRenderedMode: 'Chế độ render',
    factLastSynced: 'Đồng bộ lần cuối',

    resyncCta: 'Đồng bộ lại',
    deleteCta: 'Xóa',

    latestCrawlHeading: 'Lượt quét gần nhất',
    neverCrawledYet: 'Nguồn này chưa được quét lần nào.',
    crawlRanInMode: ({ rendered }: { rendered: boolean }) =>
      `Lượt quét này chạy ở chế độ ${rendered ? 'render' : 'chuẩn'}.`,

    failedUrlsHeading: 'URL thất bại',
    failedUrlsCountCaveat: ({ total, shown }: { total: number; shown: number }) =>
      ` — ${total} tổng cộng, hiển thị ${shown} URL đầu tiên`,
    offSiteRedirectsHeading: 'Chuyển hướng ra ngoài site',
    offSiteRedirectsMoreCaveat: ' — có thể còn nhiều hơn danh sách này hiển thị',
    nearEmptyCameBack: ({ n }: { n: number }) => `${n} trang trả về gần như không có nội dung.`,

    pagesIndexedHeading: 'Trang đã lập chỉ mục',
    nearEmptyOnlyToggle: 'Chỉ trang gần trống',

    noPagesIndexedYet: 'Chưa có trang nào được lập chỉ mục.',
    colUrl: 'URL',
    colCharacters: 'Ký tự',
    colStatus: 'Trạng thái',
    nearEmptyBadge: 'gần trống',

    indexStateIndexed: 'Đã lập chỉ mục',
    indexStateIndexing: 'Đang lập chỉ mục…',
    indexStateFailed: 'Thất bại',

    fileNoMarkdownExplainer:
      'Được lưu dưới dạng chính tệp đã tải lên — chúng tôi giữ bản gốc, không phải bản sao văn bản riêng, nên không có gì để xem trước ở đây. Tệp đã được lập chỉ mục và bot trả lời dựa trên nó.',
    nothingStoredForDocument: 'Không có gì được lưu cho tài liệu này.',

    storedContentHeading: 'Nội dung đã lưu',
    nothingStoredYet: 'Chưa có gì được lưu.',

    deleteDialogTitle: 'Xóa nguồn này?',
    deleteDialogDescriptionPrefix: 'Sẽ xóa ',
    deleteDialogDescriptionSuffix: ' và mọi thứ đã lập chỉ mục từ nó. Không thể hoàn tác.',
    cancelCta: 'Hủy',
  },
  conversations: {
    today: 'Hôm nay',
    yesterday: 'Hôm qua',
    yourTesting: 'Bạn đang dùng thử',
    visitor: 'Khách truy cập',
    playgroundTag: 'Playground',
    noMessageFallback: '(không có tin nhắn)',
    leadCapturedTag: 'Đã bắt lead',
    hasUnansweredTag: 'Có câu chưa trả lời được',
    unansweredChipLabel: 'Chưa trả lời được',
    loadingMore: 'Đang tải thêm…',
    loadMore: 'Tải thêm',
    selectConversationPrompt: 'Chọn một cuộc trò chuyện để xem nội dung.',
    backToConversations: '← Quay lại cuộc trò chuyện',
    transcriptStartedSummary: ({ time, n }: { time: string; n: number }) => `Bắt đầu ${time} · ${n} tin nhắn`,
    deleteConversationAriaLabel: 'Xóa cuộc trò chuyện này',
    leadSubmittedTag: '✓ Đã gửi lead',
    cancelCta: 'Hủy',
    deletingCta: 'Đang xóa…',

    pageTitle: 'Cuộc trò chuyện',
    headerSubtitle: ({ n }: { n: number }) => `${n} cuộc trò chuyện · mới nhất trước`,
    emptyTitle: 'Đang chờ khách truy cập đầu tiên',
    emptyDescription: 'Cuộc trò chuyện sẽ hiện ở đây ngay khi có người chat với bot của bạn.',

    deleteAllCta: 'Xóa tất cả cuộc trò chuyện',
    deleteDialogTitle: 'Xóa cuộc trò chuyện này?',
    deleteDialogDescription:
      'Xóa vĩnh viễn nội dung trò chuyện này và các tin nhắn của nó. Lead đã bắt được trong đó vẫn giữ ở mục Lead. Không thể hoàn tác.',
    deleteCta: 'Xóa cuộc trò chuyện',

    deleteAllDialogTitle: 'Xóa tất cả cuộc trò chuyện?',
    deleteAllDescriptionPrefix:
      'Xóa vĩnh viễn mọi cuộc trò chuyện và tin nhắn của bot này. Lead đã bắt vẫn giữ ở mục Lead. Không thể hoàn tác. Gõ ',
    deleteAllDescriptionSuffix: ' để xác nhận.',
    confirmBotNameAriaLabel: 'Xác nhận tên bot',
  },
  leads: {
    filterNew: 'Mới',
    filterContacted: 'Đã liên hệ',
    filterArchived: 'Đã lưu trữ',
    filterAll: 'Tất cả',
    actionMarkNew: 'Đánh dấu mới',
    actionMarkContacted: 'Đánh dấu đã liên hệ',
    actionArchive: 'Lưu trữ',
    statusNew: 'mới',
    statusContacted: 'đã liên hệ',
    statusArchived: 'đã lưu trữ',

    filterByStatusAriaLabel: 'Lọc theo trạng thái',
    pageTitle: 'Lead',
    exportingCta: 'Đang xuất…',
    exportCsvCta: 'Xuất CSV',
    exportCsvError: 'Không xuất được lead — vui lòng thử lại.',

    headerSubtitleAll: ({ n }: { n: number }) => `${n} lead đã bắt được · mới nhất trước`,
    headerSubtitleFiltered: ({ n, filterLabel }: { n: number; filterLabel: string }) =>
      `${n} lead ${filterLabel} · mới nhất trước`,

    deleteAllCta: 'Xóa tất cả lead',
    bulkActionsAriaLabel: 'Hành động hàng loạt',
    selectedCount: ({ n }: { n: number }) => `Đã chọn ${n}`,
    bulkCapMessage: ({ max }: { max: number }) => `Chọn tối đa ${max} lead mỗi lần`,
    bulkUpdatedMessage: ({ n }: { n: number }) => `Đã cập nhật ${n}`,
    bulkUpdateError: 'Không cập nhật được các lead đó. Thử lại.',

    emptyTitle: 'Đang chờ lead đầu tiên',
    emptyDescription: 'Lead do widget chat của bot bắt được sẽ hiện ở đây.',
    noFilterResultsTitle: ({ filterLabel }: { filterLabel: string }) => `Không có lead ${filterLabel}`,
    noFilterResultsDescription: 'Không có gì trong bộ lọc này lúc này.',

    selectAllAriaLabel: 'Chọn tất cả lead',
    colName: 'Tên',
    colContact: 'Liên hệ',
    colIntent: 'Nhu cầu',
    colStatus: 'Trạng thái',
    colCaptured: 'Thời gian bắt',
    colActions: 'Hành động',

    noEmailFallback: '(không có email)',
    selectLeadAriaLabel: ({ contact }: { contact: string }) => `Chọn lead ${contact}`,
    deleteLeadAriaLabel: ({ contact }: { contact: string }) => `Xóa lead ${contact}`,
    playgroundTag: 'Playground',
    viewChatCta: 'Xem đoạn chat',
    loadingMoreCta: 'Đang tải thêm…',
    loadMoreCta: 'Tải thêm',

    cancelCta: 'Hủy',
    deletingCta: 'Đang xóa…',

    deleteDialogTitle: 'Xóa lead này?',
    deleteDescriptionPrefix: 'Xóa vĩnh viễn ',
    deleteDescriptionSuffix: '. Không thể hoàn tác.',
    noContactFallback: 'lead này',
    deleteCta: 'Xóa lead',

    deleteAllDialogTitle: 'Xóa tất cả lead?',
    deleteAllDescriptionPrefix: 'Xóa vĩnh viễn mọi lead đã bắt được cho bot này. Không thể hoàn tác. Gõ ',
    deleteAllDescriptionSuffix: ' để xác nhận.',
    confirmBotNameAriaLabel: 'Xác nhận tên bot',
  },
  unanswered: {
    tabOpen: 'Đang mở',
    tabDismissed: 'Đã bỏ qua',
    tabAnswered: 'Đã trả lời',
    filterByStatusAriaLabel: 'Lọc theo trạng thái',

    pageTitle: 'Câu chưa trả lời được',
    headerSubtitle: ({ n, verb }: { n: number; verb: string }) => `${n} câu hỏi ${verb}`,
    subtitleVerbOpen: 'đang chờ trả lời',
    subtitleVerbDismissed: 'đã bỏ qua',
    subtitleVerbAnswered: 'đã trả lời',

    openTabIntro:
      'Những câu hỏi bot của bạn không trả lời được, đã gộp nhóm. Viết câu trả lời một lần — nó trở thành một nguồn Hỏi & Đáp và bot sẽ nhớ mãi.',

    emptyTitleOpen: 'Không có gì đang mở — bot của bạn đã trả lời mọi câu được hỏi.',
    emptyTitleDismissed: 'Không có câu nào đã bỏ qua.',
    emptyTitleAnswered: 'Chưa có câu nào được trả lời.',

    askedOnce: 'Hỏi một lần',
    askedTimes: ({ n }: { n: number }) => `Hỏi ${n} lần`,

    viewSourceCta: 'Xem nguồn',
    sourceRemoved: 'nguồn đã bị xóa',
    viewChatCta: 'Xem đoạn chat',
    chatDeleted: 'đoạn chat đã bị xóa',

    dismissCta: 'Bỏ qua',
    reopenCta: 'Mở lại',
    writeAnswerCta: 'Viết câu trả lời',
    cancelCta: 'Hủy',
    answerPlaceholder: 'Viết câu trả lời bạn sẽ đưa cho khách truy cập này. Bot sẽ dùng nó từ giờ trở đi.',
    addingCta: 'Đang thêm…',
    addAsQaSourceCta: 'Thêm làm nguồn Hỏi & Đáp',
    answerAddedNotice: 'Đã thêm làm nguồn Hỏi & Đáp — bot sẽ dùng nó từ cuộc trò chuyện tiếp theo.',
    saveAnswerError: 'Không lưu được câu trả lời đó — vui lòng thử lại.',
  },
  appearance: {
    pageTitle: 'Giao diện',
    pageSubtitle: 'Widget trông như thế nào và chào khách truy cập ra sao trên site của bạn',

    colorLabel: 'Màu sắc',
    colorHexAriaLabel: 'Mã màu hex',
    botNameLabel: 'Tên bot',
    botNamePlaceholder: 'Bot hỗ trợ Acme',
    welcomeMessageLabel: 'Lời chào',
    welcomePlaceholder: 'Chào bạn! Mình có thể giúp gì cho bạn hôm nay?',

    quickQuestionsLabel: 'Câu hỏi nhanh',
    addQuestionCta: 'Thêm câu hỏi',
    quickQuestionsHint: 'Không bắt buộc — hiển thị dạng chip mà khách truy cập có thể nhấn trước khi họ gõ gì.',
    quickQuestionAriaLabel: ({ n }: { n: number }) => `Câu hỏi nhanh ${n}`,
    quickQuestionPlaceholder: 'Giờ làm việc của bạn là khi nào?',
    removeCta: 'Xóa',

    positionLabel: 'Vị trí nút mở chat',
    positionBottomRight: 'Dưới bên phải',
    positionBottomLeft: 'Dưới bên trái',

    languageLabel: 'Ngôn ngữ',
    autoLanguageLabel: 'Theo trình duyệt của khách truy cập',

    badgeLabel: 'Huy hiệu "Powered by Embent"',
    badgeHint: 'Hiển thị một link nhỏ "Powered by Embent" ở chân widget.',
    badgeSwitchAriaLabel: 'Hiện huy hiệu "Powered by Embent"',
    badgeLoadError: 'Không tải được — làm mới trang',
    freeBadgeNote: ({ plans }: { plans: string }) => `Nâng cấp lên ${plans} sẽ bỏ yêu cầu này.`,

    saveCta: 'Lưu',
    savingCta: 'Đang lưu…',
    savedLabel: 'Đã lưu',

    livePreviewHeading: 'Xem trước trực tiếp',
    previewCaption:
      'Chỉ nút mở chat và bảng chat hiển thị ở đây — phần trang xung quanh để trống có chủ đích.',
    devHintPrefix: 'Nếu không thấy gì hiện lên, gói widget chưa được build cục bộ: chạy',
    devHintIn: 'trong',
    devHintAndCopy: 'và copy',
    devHintTo: 'vào',

    widgetPreviewTitle: 'Xem trước widget',
  },
  botSettings: {
    pageTitle: 'Cài đặt',
    pageSubtitle: 'Domain được phép, bắt lead, và cách bot tự giới thiệu',

    leadCaptureOffLabel: 'Tắt',
    leadCaptureOffBlurb: 'Bot không bao giờ hỏi khách truy cập thông tin liên hệ.',
    leadCaptureOnIntentLabel: 'Khi có nhu cầu',
    leadCaptureOnIntentBlurb:
      'Bot chỉ hỏi email hoặc số điện thoại khi không trả lời được hoặc khách truy cập muốn được liên hệ lại.',
    leadCaptureAlwaysLabel: 'Luôn luôn',
    leadCaptureAlwaysBlurb:
      'Bot hỏi thông tin liên hệ một lần, ngay sau lượt trao đổi đầu tiên trong cuộc trò chuyện.',
    leadCapturePreChatLabel: 'Form trước khi chat',
    leadCapturePreChatBlurb:
      'Khách truy cập điền một form liên hệ ngắn (tên, email, số điện thoại không bắt buộc) trước khi chat bắt đầu. Bot không hỏi lại trong lúc chat nữa.',

    leadLevelSubtleLabel: 'Nhẹ nhàng',
    leadLevelSubtleBlurb: 'Chỉ đề nghị liên hệ lại khi khách truy cập chủ động hỏi hoặc muốn báo giá.',
    leadLevelBalancedLabel: 'Cân bằng (khuyến nghị)',
    leadLevelBalancedBlurb:
      'Đề nghị một lần khi khách truy cập có tín hiệu mua rõ ràng — hỏi cách bắt đầu, muốn xem demo, hoặc nhắc đến site của họ.',
    leadLevelProactiveLabel: 'Chủ động',
    leadLevelProactiveBlurb:
      'Đề nghị bất cứ khi nào khách truy cập có vẻ đang cân nhắc, và hỏi lại lần nữa sau đó nếu lần đề nghị đầu chưa được hồi đáp.',

    domainRequiredError: 'Bắt buộc — xóa dòng này thay vì để trống',
    domainBareHostnameError: 'Chỉ tên miền thuần, ví dụ "example.com" (không kèm scheme hay đường dẫn)',
    domainTooLongError: 'Quá dài',

    aboutLabel: 'Giới thiệu về bot này',
    aboutHint:
      'Một đoạn giới thiệu ngắn bot luôn nhớ — nó là ai, phục vụ cho ai — để những câu như ' +
      '"bạn là ai?" được trả lời ngay mà không cần tìm kiếm.',
    aboutPlaceholder:
      'Chúng tôi là trợ lý hỗ trợ của Acme, ở đây để giúp khách hàng thiết lập và xử lý sự cố với Acme Widgets.',

    allowedDomainsLabel: 'Domain được phép',
    addDomainCta: 'Thêm domain',
    allowedDomainsHint:
      'Các site được phép nhúng widget của bot này. Để trống để cho phép mọi site — khuyến nghị ' +
      'giới hạn về đúng (các) domain của bạn trước khi lên sóng.',
    domainAriaLabel: ({ n }: { n: number }) => `Domain được phép ${n}`,
    domainPlaceholder: 'example.com',
    removeCta: 'Xóa',

    leadCaptureLabel: 'Bắt lead',
    leadLevelLabel: 'Mức độ chủ động?',

    saveCta: 'Lưu',
    savingCta: 'Đang lưu…',
    savedLabel: 'Đã lưu',

    embedSnippetHeading: 'Đoạn mã nhúng',
    embedPastePrefix: 'Dán đoạn này trước thẻ đóng',
    embedPasteSuffix: 'trên site của bạn.',

    embedCopyCta: 'Sao chép',
    embedCopiedCard: 'Đã sao chép!',
    embedCopiedCheck: '✓ Đã sao chép',

    dangerZoneHeading: 'Vùng nguy hiểm',
    dangerZoneDescription:
      'Xóa bot, các nguồn, cuộc trò chuyện, lead, và chỉ mục tìm kiếm của nó. Không thể hoàn tác.',
    deleteBotCta: 'Xóa bot này',
    deleteBotDialogTitle: ({ name }: { name: string }) => `Xóa "${name}"?`,
    dangerZoneDialogDescriptionPrefix:
      'Xóa bot, các nguồn, cuộc trò chuyện, lead, và chỉ mục tìm kiếm của nó. Không thể hoàn tác. Gõ ',
    dangerZoneDialogDescriptionSuffix: ' để xác nhận.',
    confirmBotNameAriaLabel: 'Xác nhận tên bot',
    cancelCta: 'Hủy',
    deletingCta: 'Đang xóa…',
  },
  notifications: {
    heading: 'Thông báo',
    subheading: 'Email gửi đến địa chỉ tài khoản của từng người. Hoạt động trong Playground không bao giờ gửi email.',
    noMembers: 'Không có thành viên.',
    saveError: 'Không lưu được cài đặt đó. Thử lại.',

    roleOwner: 'Chủ sở hữu',
    roleAdmin: 'Quản trị',
    roleMember: 'Thành viên',
    cadenceOff: 'Tắt',
    cadenceDaily: 'Hàng ngày',
    cadenceWeekly: 'Hàng tuần',

    colMember: 'Thành viên',
    colNewLead: 'Lead mới',
    colUnansweredDigest: 'Tổng hợp câu chưa trả lời được',
    colCrawlFailed: 'Quét thất bại',
    colWeeklySummary: 'Tổng kết hàng tuần',

    newLeadAriaLabel: ({ email }: { email: string }) => `Email lead mới cho ${email}`,
    unansweredDigestAriaLabel: ({ email }: { email: string }) => `Email tổng hợp câu chưa trả lời được cho ${email}`,
    crawlFailedAriaLabel: ({ email }: { email: string }) => `Email quét thất bại cho ${email}`,
    weeklySummaryAriaLabel: ({ email }: { email: string }) => `Email tổng kết hàng tuần cho ${email}`,
  },
  playground: {
    pageTitle: 'Playground',
    pageSubtitle: 'Trò chuyện với bot của bạn như một khách truy cập — không có gì ở đây chạm đến site của bạn.',
    retrievalDebugLabel: 'Debug truy xuất',
    hideDebugCta: 'Ẩn debug',
    newSessionCta: 'Phiên mới',
    emptyStateText: 'Gửi một tin nhắn để xem bot này trả lời như thế nào, dựa trên nguồn của nó.',
    inputPlaceholder: 'Hỏi bot này một câu…',
    stopCta: 'Dừng',
    sendCta: 'Gửi',
    debugEmptyText: 'Hỏi một câu để xem nó truy xuất được gì',
    genericError: 'Đã có lỗi xảy ra.',
  },
  billing: {
    pageTitle: 'Thanh toán',
    openingCta: 'Đang mở…',
    manageBillingCta: 'Quản lý thanh toán',

    memberGateTitle: 'Thanh toán do chủ sở hữu hoặc quản trị workspace quản lý.',
    memberGateDescription: "Nhờ chủ sở hữu hoặc quản trị thay đổi gói của workspace này.",

    nowOnPlan: ({ name }: { name: string }) => `Bạn hiện đang ở gói ${name}.`,
    finishingUp: 'Chúng tôi đang hoàn tất — làm mới trang sau một chút nếu gói chưa cập nhật.',
    confirmingWithPolar: 'Đã nhận thay đổi — đang xác nhận với Polar…',

    internalWorkspaceLabel: 'Workspace nội bộ',
    monthlyPriceLabel: ({ price }: { price: number }) => `$${price} / tháng`,
    internalWorkspaceInline: 'Không giới hạn, không áp dụng thanh toán.',
    internalWorkspaceHeading: 'Workspace nội bộ — không giới hạn, không áp dụng thanh toán.',
    internalWorkspaceBody:
      'Mọi hạn mức của gói đều được bỏ qua và không có gì để nâng cấp hay thanh toán ở đây. Mức sử dụng ' +
      'vẫn được ghi lại ở trên để bạn theo dõi, nhưng không bao giờ được đẩy sang Polar — workspace ' +
      'này không có danh tính thanh toán.',

    unlimitedWord: 'không giới hạn',
    unlimitedParens: '(không giới hạn)',
    ofCap: ({ cap }: { cap: string }) => `/ ${cap}`,
    internalRenewalFallback: 'Workspace nội bộ · không giới hạn · không áp dụng thanh toán',
    capsSuffix: ({
      bots,
      messages,
      pages,
    }: {
      bots: string;
      botsCount: number | null;
      messages: string;
      pages: string;
    }) => `${bots} bot · ${messages} tin nhắn · ${pages} trang`,
    freeForever: ({ capsSuffix }: { capsSuffix: string }) => `Miễn phí trọn đời · ${capsSuffix}`,
    renewsOn: ({ date, capsSuffix }: { date: string; capsSuffix: string }) => `Gia hạn ${date} · ${capsSuffix}`,

    cancelsAtPeriodEnd: 'Hủy vào cuối kỳ',
    planChangesPausedNotice: 'Thay đổi gói tạm dừng cho đến khi kỳ hiện tại kết thúc.',
    planChangesPausedReason:
      'Gói này đã được đặt hủy vào cuối kỳ — khôi phục trước rồi mới đổi gói được.',

    redirectingCta: 'Đang chuyển hướng…',
    switchingCta: 'Đang chuyển…',
    upgradeToCta: ({ name }: { name: string }) => `Nâng cấp lên ${name}`,

    messagesThisMonthLabel: 'Tin nhắn tháng này',
    pagesIndexedLabel: 'Trang đã lập chỉ mục',
    messageNoun: 'tin nhắn',
    pageNoun: 'trang',
    messagesUnit: ({ n }: { n: string }) => `${n} tin nhắn`,
    usageLimitWarning: ({ pct, noun }: { pct: number; noun: string }) => `${pct}% giới hạn ${noun} của bạn`,
    upgradeRaisesTo: ({ name, unit }: { name: string; unit: string }) => ` — ${name} nâng giới hạn lên ${unit}.`,
    limitReached: ' đã đạt.',

    cancelSubscriptionCta: 'Hủy gói',
    cancelingCta: 'Đang hủy…',
    cancelDialogTitle: 'Hủy gói?',
    yourCurrentPlanFallback: 'gói hiện tại của bạn',
    cancelDialogDescription: ({
      planName,
      renewsDate,
    }: {
      planName: string;
      renewsDate: string | null;
    }) =>
      `Bot của bạn vẫn hoạt động trên gói ${planName} đến hết kỳ thanh toán này${
        renewsDate ? ` (${renewsDate})` : ''
      } — sau đó workspace sẽ chuyển về Free. Việc này không bao giờ có hiệu lực ngay lập tức.`,
    keepSubscriptionCta: 'Giữ gói hiện tại',

    changePlanFallbackTitle: 'Đổi gói?',
    keepCurrentPlanCta: 'Giữ gói hiện tại',
    confirmFallbackCta: 'Xác nhận',

    checkoutErrorFallback: 'Đã có lỗi khi bắt đầu thanh toán.',
    portalErrorFallback: 'Đã có lỗi khi mở trang quản lý thanh toán.',
    cancelErrorFallback: 'Đã có lỗi khi hủy gói của bạn.',

    currentPlanBadge: 'Gói hiện tại',
    botsUnit: ({ n }: { n: number }) => `${n} bot`,
    seatsUnit: ({ n }: { n: number }) => `${n} chỗ`,
    messagesPerMonth: ({ n }: { n: string }) => `${n} tin nhắn / tháng`,
    pagesUnit: ({ n }: { n: string }) => `${n} trang`,
    badgeRequiredText: 'Huy hiệu "Powered by Embent"',
    badgeOptionalText: 'Huy hiệu "Powered by Embent" tùy chọn',
    upgradeCta: 'Nâng cấp',
    switchToCta: ({ name }: { name: string }) => `Chuyển sang ${name}`,

    flatPricingNote:
      'Giá cố định, không tính theo đầu người, không phụ phí bất ngờ — chúng tôi cảnh báo ở mức 80% và chỉ tạm dừng ' +
      'trả lời mới khi vượt 110%, và không bao giờ cắt ngang một cuộc trò chuyện.',

    planChangeConfirm: {
      verbUpgrade: 'Nâng cấp',
      verbSwitch: 'Chuyển',
      title: ({ verb, name }: { verb: string; name: string }) => `${verb} sang ${name}?`,
      confirmNow: ({ verb }: { verb: string }) => `${verb} ngay`,
      continueToPolarCta: 'Tiếp tục đến Polar',
      checkoutDescription: ({ name, price }: { name: string; price: number }) =>
        `Bạn sẽ được chuyển đến Polar để bắt đầu gói ${name} ($${price}/tháng) — Polar sẽ ` +
        'tính phí bạn ở đó để kích hoạt.',
      switchDescription: ({ name, price }: { name: string; price: number }) =>
        `Việc này đổi gói của bạn ngay lập tức — bot của bạn bắt đầu dùng giới hạn của ${name} ` +
        `($${price}/tháng) ngay. Polar sẽ không tính phí hôm nay: khoản chênh lệch theo tỷ lệ ` +
        'sẽ được cộng vào hóa đơn tiếp theo của bạn.',
    },
  },
  workspace: {
    pageTitle: 'Cài đặt workspace',
    loadErrorMessage: 'Không tải được workspace này. Tải lại trang để thử lại.',
    notAllowedTitle: 'Chỉ chủ sở hữu và quản trị mới có thể thay đổi cài đặt workspace.',
    notAllowedDescription: 'Nhờ chủ sở hữu hoặc quản trị của workspace này thực hiện thay đổi.',

    nameLabel: 'Tên workspace',
    nameHint: ({ min, max }: { min: number; max: number }) =>
      `${min}–${max} ký tự. Hiển thị cho mọi người trong workspace này và trong email mời.`,

    uploadLogoCta: 'Tải logo lên',
    removeLogoCta: 'Xóa',
    logoHint: 'Ảnh vuông là đẹp nhất. PNG, JPEG, WebP hoặc GIF, tối đa 2 MB.',
    logoNotConfiguredError: 'Tải logo chưa được cấu hình trên môi trường này.',
    uploadLogoErrorFallback: 'Không tải logo lên được. Thử lại.',
    removeLogoErrorFallback: 'Không xóa được logo. Thử lại.',

    saveNameErrorFallback: 'Không lưu được tên. Thử lại.',
    saveCta: 'Lưu',
    savedLabel: 'Đã lưu',

    cropDialogTitle: 'Cắt logo của bạn',
    cropDialogDescription: 'Kéo để định vị lại, thu phóng để vừa khung. Lưu dưới dạng hình vuông 256×256.',
    zoomLabel: 'Thu phóng',
    cropErrorFallback: 'Không cắt được ảnh này. Thử một tệp khác.',
    cancelCta: 'Hủy',
    useThisLogoCta: 'Dùng logo này',
  },
  members: {
    pageTitle: 'Thành viên',
    membersOnlyTitle: 'Khu vực chỉ dành cho thành viên',
    membersOnlyDescription: 'Nhờ chủ sở hữu workspace hoặc quản trị quản lý thành viên.',
    unlimitedCapWord: 'Không giới hạn',
    seatsSubtitle: ({
      used,
      cap,
      pending,
    }: {
      used: number;
      cap: string | number;
      pending: number;
    }) => `Đã dùng ${used}/${cap} chỗ${pending > 0 ? ` (${pending} đã mời)` : ''}`,
    inviteMemberCta: 'Mời thành viên',

    roleOwner: 'Chủ sở hữu',
    roleAdmin: 'Quản trị',
    roleMember: 'Thành viên',
    roleOptionMember: 'Thành viên',
    roleOptionAdmin: 'Quản trị',

    colName: 'Tên',
    colEmail: 'Email',
    colRole: 'Vai trò',
    leaveWorkspaceCta: 'Rời workspace',
    removeCta: 'Xóa',

    pendingInvitesHeading: 'Lời mời đang chờ',
    noPendingInvites: 'Không có lời mời nào đang chờ.',
    inviteColEmail: 'Email',
    inviteColRole: 'Vai trò',
    inviteColExpires: 'Hết hạn',
    revokingCta: 'Đang thu hồi…',
    revokeCta: 'Thu hồi',

    cancelCta: 'Hủy',
    removingCta: 'Đang xóa…',
    leaveDialogTitle: 'Rời workspace này?',
    removeDialogTitle: ({ name }: { name: string }) => `Xóa ${name}?`,
    leaveDialogDescription: 'Bạn sẽ mất quyền truy cập ngay lập tức — chủ sở hữu hoặc quản trị có thể mời bạn lại sau.',
    removeDialogDescription: ({ name }: { name: string }) => `${name} sẽ mất quyền truy cập workspace này ngay lập tức.`,

    emailLabel: 'Email',
    emailPlaceholder: 'teammate@example.com',
    roleLabel: 'Vai trò',
    inviteErrorFallback: 'Đã có lỗi khi gửi lời mời này.',
    sendingInviteCta: 'Đang gửi…',
    sendInviteCta: 'Gửi lời mời',

    roleChangeErrorFallback: 'Không đổi được vai trò này.',
    removeErrorFallback: 'Không xóa được thành viên này.',
    revokeErrorFallback: 'Không thu hồi được lời mời này.',
  },
  account: {
    pageTitle: 'Tài khoản',
    loadSignInMethodsError: 'Không tải được phương thức đăng nhập',
    emailPasswordLabel: 'Email & mật khẩu',
    googleLabel: 'Google',

    savingCta: 'Đang lưu…',

    profileHeading: 'Hồ sơ',
    displayNameLabel: 'Tên hiển thị',
    namePlaceholder: 'Tên của bạn',
    emailLabel: 'Email',
    saveNameErrorFallback: 'Đã có lỗi khi lưu tên của bạn.',
    saveCta: 'Lưu',
    savedLabel: 'Đã lưu',

    passwordHeading: 'Mật khẩu',
    passwordMismatchError: 'Mật khẩu mới và xác nhận không khớp.',
    currentPasswordLabel: 'Mật khẩu hiện tại',
    newPasswordLabel: 'Mật khẩu mới',
    confirmNewPasswordLabel: 'Xác nhận mật khẩu mới',
    changePasswordErrorFallback: 'Đã có lỗi khi đổi mật khẩu của bạn.',
    changePasswordCta: 'Đổi mật khẩu',
    passwordChangedLabel: 'Đã đổi mật khẩu',
  },
  adminImpersonate: {
    impersonateFailedFallback: 'Không thể mạo danh người dùng này.',
    lookupFailedFallback: 'Không tra cứu được người dùng này.',
    userNotFound: 'Không tìm thấy tài khoản người dùng.',
    noOwnerToView: 'Workspace này không có chủ sở hữu để xem với vai trò đó.',
  },
  admin: {
    pagination: {
      pageOfTotal: ({
        page,
        totalPages,
        totalCount,
      }: {
        page: number;
        totalPages: number;
        totalCount: string;
      }) => `Trang ${page}/${totalPages} · tổng ${totalCount}`,
      prevCta: 'Trước',
      nextCta: 'Sau',
    },
    workspaces: {
      crawlSignalRenderedLabel: 'Phụ thuộc vào quét ở chế độ render',
      crawlSignalSourcesAcrossBots: ({
        botCountLabel,
      }: {
        sourceCount: number;
        botCountLabel: string;
        botCount: number;
      }) => `nguồn trên ${botCountLabel} bot`,
      crawlSignalRenderedFootnote:
        'Bao nhiêu phần của hệ thống bot đang dựa vào Cloudflare Browser Rendering — dung lượng chúng tôi có trên Workers Free hiện tại, chưa phải Paid.',
      crawlSignalCapacityLabel: 'Chạm giới hạn dung lượng render của chúng tôi (7 ngày)',
      // `n` only drove English's singular/plural branch; Vietnamese has none, so the param is
      // unused but kept in the signature (glossary §6) to match `en.ts`'s function shape exactly.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      crawlSignalSourceCountLabel: ({ n }: { n: number }) => 'nguồn',
      crawlSignalCapacityFootnote:
        "Chỉ tính riêng nguồn, chỉ dung lượng của chúng tôi — không bao giờ là hạn mức gói của khách hàng. Một tiến trình bị kill hoặc một job cũ bị dọn dẹp sẽ không để lại bản ghi — đây là con số sàn. Tăng đều đặn nghĩa là đã đến lúc chuyển sang Workers Paid ($5/tháng).",

      emptyText: 'Chưa có workspace nào.',
      internalBadge: 'NỘI BỘ',
      cancelsAtPeriodEndHint: 'hủy vào cuối kỳ',
      colName: 'Tên',
      colOwner: 'Chủ sở hữu',
      colPlan: 'Gói',
      colBots: 'Bot',
      colMembers: 'Thành viên',
      colMsgsPerMo: 'Tin nhắn / tháng',
      colPages: 'Trang',
      colCreated: 'Tạo lúc',
      colActions: 'Hành động',
      noOwnerTitle: 'Workspace này không có chủ sở hữu',
      viewAsCta: 'Xem với vai trò',
      switchingCta: 'Đang chuyển…',
      viewAsErrorFallback: 'Thất bại',

      backToWorkspaces: '← Quay lại Workspace',
      ownerLabel: 'Chủ sở hữu: ',
      bannedBadge: 'đã cấm',
      createdLabel: ({ date }: { date: string }) => `Tạo lúc ${date}`,
      usageHeading: 'Mức sử dụng',
      messagesThisMonthLabel: 'Tin nhắn tháng này',
      pagesIndexedLabel: 'Trang đã lập chỉ mục',
      botsLabel: 'Bot',
      internalUsageNote: 'Workspace nội bộ — không áp dụng hạn mức; mức sử dụng vẫn được ghi lại ở trên.',
      botsHeading: 'Bot',
      noBotsYet: 'Chưa có bot nào.',
      colBotName: 'Tên',
      colPublicId: 'ID công khai',
      colStatus: 'Trạng thái',
      colBotCreated: 'Tạo lúc',
      membersHeading: 'Thành viên',
      noMembersYet: 'Không có thành viên.',
      colEmail: 'Email',
      colMemberName: 'Tên',
      colRole: 'Vai trò',
      colJoined: 'Tham gia lúc',
      memberRoleLabel: {
        owner: 'Chủ sở hữu',
        admin: 'Quản trị',
        member: 'Thành viên',
      },
      subscriptionHeading: 'Gói đăng ký',
      statusLabel: 'Trạng thái: ',
      periodEndLabel: 'Kết thúc kỳ: ',
      cancelsAtPeriodEndLabel: 'Hủy vào cuối kỳ: ',
      yesLabel: 'Có',
      noLabel: 'Không',
      polarSubscriptionIdLabel: 'Mã gói đăng ký Polar: ',
      manageInPolarLink: 'Quản lý trong Polar ↗',
      noSubscriptionText: 'Không có gói đăng ký — workspace này đang ở gói miễn phí.',
      dangerZoneHeading: 'Vùng nguy hiểm',
      internalToggleLabel: 'Workspace nội bộ',
      internalToggleHint: 'Không hạn mức, không thanh toán — mức sử dụng vẫn được ghi lại nhưng không đẩy sang Polar.',
      banOwnerLabel: 'Cấm chủ sở hữu',
      banOwnerHint: 'Người dùng bị cấm không thể đăng nhập. Widget/API công khai không bị ảnh hưởng.',
      unbanCta: 'Bỏ cấm',
      unbanningCta: 'Đang bỏ cấm…',
      banCta: 'Cấm',
      demoteBeforeBanTitle: 'Hạ quyền quản trị này xuống user trước khi cấm.',
      roleLabel: 'Vai trò',
      roleHint: 'Quản trị có thể truy cập mọi màn hình admin nội bộ.',
      cannotChangeOwnRoleTitle: 'Bạn không thể đổi vai trò của chính mình.',
      cancelCta: 'Hủy',
      savingCta: 'Đang lưu…',
      confirmCta: 'Xác nhận',
      markInternalDialogTitle: 'Đánh dấu workspace này là nội bộ?',
      removeInternalDialogTitle: 'Bỏ trạng thái nội bộ?',
      markInternalDialogDescription:
        'Workspace này sẽ bỏ qua mọi hạn mức của gói (bot, tin nhắn, trang) và sẽ không bao giờ bị tính phí. Mức sử dụng vẫn được ghi lại để theo dõi, nhưng lượt đẩy sử dụng lên Polar hàng đêm sẽ bỏ qua nó — không có danh tính thanh toán để đẩy tới.',
      removeInternalDialogDescription:
        'Workspace này sẽ quay lại áp dụng hạn mức gói và thanh toán bình thường (nếu có gói đăng ký đang hoạt động).',
      banDialogTitle: ({ email }: { email: string | undefined }) => `Cấm ${email}?`,
      banDialogDescription: 'Việc này sẽ đăng xuất họ ngay lập tức và chặn mọi lượt đăng nhập sau này cho đến khi được bỏ cấm.',
      banReasonLabel: 'Lý do (không bắt buộc)',
      banReasonPlaceholder: 'Vì sao tài khoản này bị cấm?',
      banningCta: 'Đang cấm…',
      banUserCta: 'Cấm người dùng',
      roleDialogTitle: ({ role }: { role: 'user' | 'admin' | null }) => `Đổi vai trò thành ${role}?`,
      roleDialogDescriptionAdmin: ({ email }: { email: string | undefined }) =>
        `${email} sẽ có quyền truy cập mọi màn hình admin nội bộ.`,
      roleDialogDescriptionUser: ({ email }: { email: string | undefined }) =>
        `${email} sẽ mất quyền truy cập các màn hình admin nội bộ.`,
      updateWorkspaceErrorFallback: 'Không cập nhật được workspace này.',
      banUserErrorFallback: 'Không cấm được người dùng này.',
      unbanUserErrorFallback: 'Không bỏ cấm được người dùng này.',
      changeRoleErrorFallback: 'Không đổi được vai trò của người dùng này.',
    },
    bots: {
      colName: 'Tên',
      colPublicId: 'ID công khai',
      colWorkspace: 'Workspace',
      colStatus: 'Trạng thái',
      colSources: 'Nguồn',
      colLastCrawl: 'Lần quét cuối',
      colInstanceId: 'ID instance',
      noBotsYet: 'Chưa có bot nào.',

      backToBots: '← Quay lại Bot',
      workspaceLabel: 'Workspace: ',
      instanceLabel: ({ id }: { id: string }) => `Instance: ${id}`,
      createdLabel: ({ date }: { date: string }) => `Tạo lúc ${date}`,
      openOwnerViewCta: 'Mở góc nhìn của chủ sở hữu',
      openingCta: 'Đang mở…',
      openOwnerViewErrorFallback: 'Không mở được góc nhìn của chủ sở hữu.',
      sourcesHeading: 'Nguồn',
      noSourcesYet: 'Chưa có nguồn nào.',
      colUrl: 'URL',
      colType: 'Loại',
      colSourceStatus: 'Trạng thái',
      colPages: 'Trang',
      colLastCrawled: 'Quét lần cuối',
      colError: 'Lỗi',
      recentJobsHeading: 'Tác vụ gần đây',
      noJobsLinked: 'Chưa có tác vụ nào liên kết với bot này.',
      colKind: 'Loại',
      colJobStatus: 'Trạng thái',
      colAttempts: 'Số lần thử',
      colUpdated: 'Cập nhật lúc',
      colPayload: 'Payload',
      recentJobsFootnote:
        "Tác vụ gần đây được khớp theo cách best-effort qua payload của từng tác vụ — một tác vụ không mang id của bot này hoặc id của một trong các nguồn của nó sẽ không hiện ở đây, kể cả khi nó có liên quan gián tiếp đến bot này.",
    },
    jobs: {
      tabLabel: {
        all: 'Tất cả',
        running: 'Đang chạy',
        failed: 'Thất bại',
      },
      noFailedJobs: 'Không có tác vụ thất bại — hàng đợi đang khỏe mạnh.',
      noJobsToShow: 'Không có tác vụ nào để hiển thị.',
      colKind: 'Loại',
      colStatus: 'Trạng thái',
      colPayload: 'Payload',
      colAttempts: 'Số lần thử',
      colCreated: 'Tạo lúc',
      colUpdated: 'Cập nhật lúc',
      colActions: 'Hành động',
      retryCta: 'Thử lại',
      retryingCta: 'Đang thử lại…',
      cancelCta: 'Hủy',
      cancellingCta: 'Đang hủy…',
      cancelJobCta: 'Hủy tác vụ',
      keepJobCta: 'Giữ tác vụ',
      cancelDialogTitle: 'Hủy tác vụ này?',
      retryJobErrorFallback: 'Không thử lại được tác vụ này.',
      cancelJobErrorFallback: 'Không hủy được tác vụ này.',
      cancelDialogDescriptionListRow:
        "Việc này xóa dòng tác vụ đang chờ — nó sẽ không bao giờ chạy. Chỉ tác vụ đang chờ mới hủy được theo cách này; một tác vụ đang chạy có thể đã có handler xử lý dở dang.",

      backToJobs: '← Quay lại Tác vụ',
      jobIdLabel: 'ID tác vụ',
      attemptsLabel: 'Số lần thử',
      createdLabel: 'Tạo lúc',
      updatedLabel: 'Cập nhật lúc',
      runAfterLabel: 'Chạy sau',
      errorHeading: 'Lỗi',
      payloadHeading: 'Payload',
      cancelDialogDescriptionDetailPage: 'Việc này xóa dòng tác vụ đang chờ — nó sẽ không bao giờ chạy.',
    },
    posts: {
      description:
        'Bài viết blog, bản nháp và đã xuất bản. Chỉnh sửa ở đây sẽ lên embent.com trong vòng một phút sau khi xuất bản.',
      newPostCta: 'Bài viết mới',
      emptyText: 'Chưa có bài viết nào — tạo bài đầu tiên.',
      colTitle: 'Tiêu đề',
      colStatus: 'Trạng thái',
      colAuthor: 'Tác giả',
      colPublished: 'Xuất bản lúc',
      colUpdated: 'Cập nhật lúc',
      scheduledLabel: ({ date }: { date: string }) => `đã lên lịch · ${date}`,
    },
    postEditor: {
      backToPosts: '← Bài viết',
      newPostTitle: 'Bài viết mới',
      editPostTitleFallback: 'Sửa bài viết',

      titleLabel: 'Tiêu đề',
      titlePlaceholder: 'Embent trả lời câu hỏi hỗ trợ như thế nào',
      slugLabel: 'Slug',
      slugPlaceholder: 'how-embent-answers-support-questions',
      slugUrlPrefix: 'embent.com/blog/',
      excerptLabel: 'Tóm tắt',
      excerptPlaceholder: 'Một hoặc hai câu — hiển thị ở /blog và dùng làm meta description.',
      tagsLabel: 'Thẻ',
      tagsPlaceholder: 'rag, support, launch',
      tagsHint: 'Ngăn cách bằng dấu phẩy.',
      coverUrlLabel: 'URL ảnh bìa',
      coverUrlPlaceholder: 'https://cdn.embent.com/blog/…',

      authorLabel: 'Tác giả',
      editAuthorCta: 'Sửa tác giả',
      newAuthorCta: '+ Tác giả mới',
      authorSelectPlaceholder: 'Chọn một tác giả',
      noAuthorsYet: 'Chưa có tác giả nào — thêm một tác giả bằng "+ Tác giả mới" ở trên.',

      visibilityScheduledText: ({ date }: { date: string }) => `Lên sóng ${date}`,
      visibilityLiveText: ({ date }: { date: string }) => `Xuất bản ${date}`,
      visibilityDraftText: 'Chưa xuất bản',
      publishDateLabel: 'Ngày xuất bản',
      publishDateHintPrefix: 'Giờ tính theo ',
      publishDateHintSuffix:
        " — đồng hồ của trình duyệt này. Để trống để lên sóng ngay khi bạn xuất bản. Đặt một thời điểm trong tương lai và bài viết sẽ ẩn cho đến lúc đó — nó biến mất khỏi blog, số lượng theo thẻ, sitemap và RSS feed cho đến khi đồng hồ chạm tới. Không cần gì phải chạy để nó xuất hiện.",

      bodyLabel: 'Nội dung (Markdown)',
      livePreviewLabel: 'Xem trước trực tiếp',

      missingFieldLabel: {
        title: 'tiêu đề',
        slug: 'slug',
        excerpt: 'tóm tắt',
        body: 'nội dung',
        author: 'tác giả',
      },
      beforeSavingAdd: ({ fields }: { fields: string }) => `Trước khi lưu, hãy thêm: ${fields}.`,

      savingCta: 'Đang lưu…',
      saveChangesCta: 'Lưu thay đổi',
      saveDraftCta: 'Lưu bản nháp',
      unpublishingCta: 'Đang gỡ xuất bản…',
      schedulingCta: 'Đang lên lịch…',
      publishingCta: 'Đang xuất bản…',
      unpublishCta: 'Gỡ xuất bản',
      scheduleCta: 'Lên lịch',
      publishCta: 'Xuất bản',
      savedFlash: 'Đã lưu',

      renderingPreview: 'Đang dựng…',
      previewFailedPrefix: 'Xem trước lỗi: ',

      uploadFailedFallback: 'Tải lên thất bại',
      bodyPlaceholder: 'Viết bằng Markdown… dán hoặc kéo một ảnh vào để tải lên trực tiếp.',
      uploadingText: 'Đang tải lên…',
      imageUploadFailedPrefix: 'Tải ảnh lên thất bại: ',

      deleteCta: 'Xóa',
      deleteDialogTitle: ({ title }: { title: string }) => `Xóa "${title}"?`,
      deleteDialogDescriptionPrefix: 'Xóa vĩnh viễn bài viết này. Không thể hoàn tác. Gõ',
      deleteDialogDescriptionSuffix: ' để xác nhận.',
      confirmPostTitleAriaLabel: 'Xác nhận tiêu đề bài viết',
      cancelCta: 'Hủy',
      deletingCta: 'Đang xóa…',
      deletePostCta: 'Xóa bài viết',

      editAuthorDialogTitle: ({ name }: { name: string }) => `Sửa ${name}`,
      newAuthorDialogTitle: 'Tác giả mới',
      authorDialogDescription: 'Chỉ dùng bút danh có thật — không tạo danh tính giả.',
      authorNameLabel: 'Tên',
      authorNamePlaceholder: 'Johnny Bui',
      authorRoleLabel: 'Vai trò',
      authorRolePlaceholder: 'Nhà sáng lập',
      authorAvatarLabel: 'URL ảnh đại diện',
      authorAvatarPlaceholder: 'https://cdn.embent.com/…',
      authorBioLabel: 'Tiểu sử',
      authorPersonaLabel: 'Phong cách viết (nội bộ)',
      authorPersonaPlaceholder:
        'Viết câu ngắn, trực tiếp. Hoài nghi với sự phóng đại. Thích một ví dụ cụ thể hơn là một tính từ.',
      authorPersonaHint:
        "Một ghi chú định hướng để viết theo giọng của bút danh này — không bao giờ xuất bản, không bao giờ hiển thị cho độc giả.",
      authorSavingCta: 'Đang lưu…',
      authorAddingCta: 'Đang thêm…',
      saveAuthorCta: 'Lưu tác giả',
      addAuthorCta: 'Thêm tác giả',

      writingGuideHeading: 'Hướng dẫn viết bài',
      writingGuideSubtitle: 'Hỗ trợ Markdown, hình ảnh, và một prompt định hướng AI',
      supportedMarkdownHeading: 'Markdown được hỗ trợ',
      mdListHeadings: 'Tiêu đề, cấp 1 đến 6',
      mdListLists: 'Danh sách gạch đầu dòng và đánh số',
      mdListLinksPrefix: 'Link — ',
      mdListLinksSuffix: ', chỉ http(s) và mailto',
      mdListImages: 'Hình ảnh ở bất kỳ đâu trong bài, kể cả giữa bài — dán hoặc kéo một ảnh vào (xem bên dưới)',
      mdListCode: 'Code inline và code block, có tô màu cú pháp',
      mdListTables: 'Bảng, kiểu GitHub-flavored, có căn lề cột',
      mdListBlockquotes: 'Trích dẫn khối',
      strippedHeading: 'Những gì bị loại bỏ',
      strippedPrefix: 'Mọi thứ khác. Thẻ HTML thô, và nội dung ',
      strippedSuffix:
        " nói riêng, không bao giờ tồn tại — nội dung được lọc theo một danh sách cố định (tiêu đề, danh sách, link, ảnh, code, bảng, trích dẫn khối, cộng với đoạn văn/nhấn mạnh mà chúng được dựng từ đó) trước khi hiển thị bất cứ đâu, kể cả ở đây khi xem trước.",
      imagesHeading: 'Hình ảnh',
      imagesPrefix: 'Dán hoặc kéo một ảnh vào nội dung — nó sẽ tải lên cdn.embent.com và chèn',
      imagesSuffix:
        ' vào đúng vị trí con trỏ, dù con trỏ ở đâu, để nó có thể nằm giữa đoạn văn. Ảnh bìa ở trên là một trường riêng — nó không bao giờ lấy từ thứ được thả vào nội dung.',
      copiedCta: 'Đã sao chép!',
      copyAiPromptCta: 'Sao chép prompt AI',
      copiedToClipboard: 'Đã sao chép vào clipboard.',
      clipboardUnavailable: "Clipboard không khả dụng trên trình duyệt này — chọn và copy prompt bên dưới.",
      assemblesPrefix: 'Gộp phong cách chung, ',
      assemblesAuthorPersonaClause: ({ authorName }: { authorName: string }) => `phong cách viết của ${authorName}`,
      assemblesNoAuthorClause: 'một ghi chú rằng chưa chọn tác giả',
      assemblesSuffix:
        ', các quy ước Markdown ở trên, và một cấu trúc yêu cầu — dán vào một model bên ngoài, rồi dán bản nháp trở lại vào nội dung.',

      aiPromptPersonaWithVoice: ({ authorName, persona }: { authorName: string; persona: string }) =>
        `Viết theo giọng của ${authorName}:\n${persona}`,
      aiPromptPersonaNoPersona: ({ authorName }: { authorName: string }) =>
        `${authorName} chưa có phong cách viết được thiết lập — viết theo giọng đơn giản, trực tiếp.`,
      aiPromptAuthorFallbackLower: 'tác giả này',
      aiPromptAuthorFallbackUpper: 'Tác giả này',
      aiPromptTemplate: ({ personaSection }: { personaSection: string }) =>
        `Bạn đang viết một bài blog cho Embent (embent.com), một nền tảng AI agent cho website.

Phong cách chung:
- Trực tiếp và cụ thể. Không hype, không màu mè quảng cáo.
- Đoạn văn ngắn (2-4 câu).
- Không bao giờ bịa số liệu hay tên khách hàng. Nếu không có con số thật hay khách hàng thật, đừng tự nghĩ ra.

Giọng của tác giả:
${personaSection}

Quy ước Markdown (đây là TẤT CẢ những gì sẽ hiển thị — mọi thứ khác sẽ bị loại bỏ):
- Tiêu đề (#, ##, ###), danh sách gạch đầu dòng và đánh số, trích dẫn khối.
- Link dạng [text](url) — chỉ http(s) và mailto.
- Hình ảnh ở bất kỳ đâu trong bài, kể cả giữa bài, dạng ![alt](url).
- Code inline và code block.
- Bảng (kiểu GitHub-flavored).
- Thẻ HTML thô và <script>/<style> sẽ bị loại bỏ hoàn toàn — chỉ viết Markdown thuần, không HTML.

Cấu trúc yêu cầu:
1. Tiêu đề
2. Một đoạn mở đầu hấp dẫn
3. 4-7 phần, mỗi phần có tiêu đề h2 (##)
4. Một đoạn kết ngắn

Viết toàn bộ bản nháp ngay bây giờ dưới dạng Markdown.`,
    },
    feedback: {
      filterLabel: {
        unhandled: 'Chưa xử lý',
        all: 'Tất cả',
      },
      typeLabel: {
        bug: 'Lỗi',
        feature: 'Tính năng',
        question: 'Câu hỏi',
      },
      mailtoSubject: ({ id, type }: { id: string; type: string }) => `Re: [Embent feedback ${id}] ${type}`,
      replyCta: 'Trả lời',
      colDate: 'Ngày',
      colWorkspace: 'Workspace',
      colUser: 'Người dùng',
      colType: 'Loại',
      colMessage: 'Nội dung',
      colActions: 'Hành động',
      emptyUnhandled: 'Không có phản hồi chưa xử lý — hòm thư sạch.',
      emptyAll: 'Chưa có phản hồi nào.',
      updateFeedbackErrorFallback: 'Không cập nhật được phản hồi này.',
      reopeningCta: 'Đang mở lại…',
      markingCta: 'Đang đánh dấu…',
      reopenCta: 'Mở lại',
      markHandledCta: 'Đánh dấu đã xử lý',
      noBotFallback: 'không có bot',
      noUaFallback: 'không có UA',
    },
    playground: {
      demosAllTimeLabel: 'Demo mọi thời điểm',
      demos30dLabel: 'Demo · 30 ngày',
      messagesAllTimeLabel: 'Tin nhắn mọi thời điểm',
      signedUpAllTimeLabel: 'Đã đăng ký',
      signedUp30dLabel: 'Đã đăng ký · 30 ngày',
      leadsAllTimeLabel: 'Lead đã bắt được',
      leads30dLabel: 'Lead · 30 ngày',
      demosPerDayLabel: 'Demo mỗi ngày',
      messagesPerDayLabel: 'Tin nhắn mỗi ngày',
      demoUnit: () => 'demo',
      messageUnit: () => 'tin nhắn',
      noDemos30d: 'Không có demo nào trong 30 ngày qua.',
      statusBreakdownHeading: 'Phân bổ trạng thái (30 ngày)',
      recentDomainsHeading: 'Domain gần đây',
      noDemosYet: 'Chưa có demo nào.',
      colHostname: 'Domain',
      colStatus: 'Trạng thái',
      colPages: 'Trang',
      colMessages: 'Tin nhắn',
      colAttempts: 'Số lần thử',
      colSignedUp: 'Đã đăng ký',
      colLeads: 'Lead',
      colCreated: 'Tạo lúc',
      signedUpAriaLabel: 'đã đăng ký',
      notSignedUpAriaLabel: 'chưa đăng ký',
      noEmailFallback: '(không có email)',
    },
  },
} satisfies Catalog;
